import invariant from 'invariant'
import type { Activity, Enrollment } from '../types/GrooverTypes'

const QR_DATA_DELIMITER = '/'
const QR_DATA_HASH_LENGHT = 3

export type QRType = 'visit' | 'payment' | 'singleEvent' | 'dropin' | 'unknown'

export type QRCodeCheckResult = 'valid' | 'expired' | 'invalid_data' | 'invalid_hash'

export type QRCheckResult = {
  success: boolean
  code: QRCodeCheckResult
  type: QRType
  id?: string
}

export type QRSingleEventCheckResult = QRCheckResult & {
  type: 'singleEvent'
  enrollmentId: string
  ticketIndex: number
}

export type QRRequestBase = {
  type: QRType
}

export type QRRequestVisit = QRRequestBase & {
  type: 'visit'
  data: {
    enrollmentId: string
  }
}

export type QRRequestSingleEvent = QRRequestBase & {
  type: 'singleEvent'
  data: {
    enrollmentId: string
    ticketIndex: number // 1-based index for multiple tickets of same activity
  }
}

export type QRRequestPayment = QRRequestBase & {
  type: 'payment'
  data: {
    userId: string
  }
}

export type QRRequest = QRRequestVisit | QRRequestSingleEvent | QRRequestPayment

const calculateDateString = (timestamp?: Date) => {
  const dateString = (timestamp || new Date()).toISOString().slice(0, 10).replace(/-/g, '')
  return dateString
}

const calculateHash = (data: string) => {
  return data
    .split('')
    .reduce((acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) | 0, 0)
    .toString(36)
    .replace('-', '')
    .slice(0, QR_DATA_HASH_LENGHT)
    .toUpperCase()
}

const buildDataString = (request: QRRequest, dateString: string) => {
  switch (request.type) {
    case 'visit':
      invariant(request.data.enrollmentId, 'enrollmentId is required')
      return `v${QR_DATA_DELIMITER}${request.data.enrollmentId}${QR_DATA_DELIMITER}${dateString}`
    case 'singleEvent':
      invariant(request.data.enrollmentId, 'enrollmentId is required')
      invariant(request.data.ticketIndex, 'ticketIndex is required')
      return `s${QR_DATA_DELIMITER}${request.data.enrollmentId}${QR_DATA_DELIMITER}${request.data.ticketIndex}${QR_DATA_DELIMITER}${dateString}`
    default:
      return 'INVALID'
  }
}

export const getQRCode = (request: QRRequest, timestamp?: Date) => {
  switch (request.type) {
    case 'visit': {
      const dateStamp = calculateDateString(timestamp)
      const qrData = buildDataString(request, dateStamp)
      const hash = calculateHash(qrData)
      return `${qrData}${QR_DATA_DELIMITER}${hash}`
    }

    case 'singleEvent': {
      const dateStamp = calculateDateString(timestamp)
      const qrData = buildDataString(request, dateStamp)
      const hash = calculateHash(qrData)
      return `${qrData}${QR_DATA_DELIMITER}${hash}`
    }

    default:
      return 'UNKOWN_QR_TYPE'
  }
}

export const checkQRCode = (qrData: string): QRCheckResult => {
  const [qrType, ...otherDataParts] = qrData.split(QR_DATA_DELIMITER)
  const otherData = otherDataParts.join(QR_DATA_DELIMITER)

  switch (qrType) {
    case 'd': {
      // Drop-in ticket format: d/<dropinTicketId>
      const ticketId = otherData.trim()
      if (!ticketId) {
        return {
          success: false,
          code: 'invalid_data',
          type: 'dropin'
        }
      }
      return { success: true, code: 'valid', type: 'dropin', id: ticketId }
    }

    case 'v': {
      const [enrollmentId, dateStamp, hash] = otherData.split(QR_DATA_DELIMITER)
      const request: QRRequestVisit = {
        type: 'visit',
        data: { enrollmentId }
      }

      const data = buildDataString(request, dateStamp) // checkTimestamp)
      const expectedHash = calculateHash(data).split(QR_DATA_DELIMITER).pop()

      // Check data format
      if (!qrType || !enrollmentId || !dateStamp || !hash) {
        return {
          success: false,
          code: 'invalid_data',
          type: 'visit'
        }
      }

      // Check expiration
      // if (dateStamp !== checkTimestamp) {
      //   return { success: false, code: 'expired', type: 'visit' }
      // }

      // Check hash
      if (hash !== expectedHash) {
        return { success: false, code: 'invalid_hash', type: 'visit' }
      }

      return { success: true, code: 'valid', type: 'visit', id: enrollmentId }
    }

    case 's': {
      const [enrollmentId, ticketIndex, dateStamp, hash] = otherData.split(QR_DATA_DELIMITER)

      // Check data format first
      if (!qrType || !enrollmentId || !ticketIndex || !dateStamp || !hash) {
        return {
          success: false,
          code: 'invalid_data',
          type: 'singleEvent'
        }
      }

      const ticketIndexNum = parseInt(ticketIndex, 10)
      if (isNaN(ticketIndexNum) || ticketIndexNum <= 0) {
        return {
          success: false,
          code: 'invalid_data',
          type: 'singleEvent'
        }
      }

      const request: QRRequestSingleEvent = {
        type: 'singleEvent',
        data: { enrollmentId, ticketIndex: ticketIndexNum }
      }

      const data = buildDataString(request, dateStamp)
      const expectedHash = calculateHash(data).split(QR_DATA_DELIMITER).pop()

      // Check hash
      if (hash !== expectedHash) {
        return { success: false, code: 'invalid_hash', type: 'singleEvent' }
      }

      return {
        success: true,
        code: 'valid',
        type: 'singleEvent',
        id: `${enrollmentId}:${ticketIndex}`,
        enrollmentId,
        ticketIndex: ticketIndexNum
      } as QRSingleEventCheckResult
    }

    default:
      return { success: false, code: 'invalid_data', type: 'unknown' }
  }
}

/**
 * Helper function to generate multiple QR codes for SingleEvent enrollments
 * based on activityQuantities from the enrollment
 *
 * Example usage:
 * const enrollment = {
 *   id: 'enrollment123',
 *   activities: [
 *     { id: 'activity1', eventType: 'SingleEvent' },
 *     { id: 'activity2' } // Regular activity
 *   ],
 *   activityQuantities: { 'activity1': 3 } // 3 tickets for activity1
 * }
 *
 * const qrCodes = generateQRCodesForEnrollment(enrollment)
 * // Returns:
 * // [
 * //   {
 * //     activityId: 'activity1',
 * //     qrCodes: ['s/enrollment123/1/20250729/ABC', 's/enrollment123/2/20250729/DEF', 's/enrollment123/3/20250729/GHI']
 * //   },
 * //   {
 * //     activityId: 'activity2',
 * //     qrCodes: ['v/enrollment123/20250729/JKL']
 * //   }
 * // ]
 */
export const generateQRCodesForEnrollment = (
  enrollment: {
    id: string
    activityQuantities?: Enrollment['activityQuantities']
    activities: { id: string; eventType?: Activity['eventType'] }[]
  },
  timestamp?: Date
): { activityId: string; qrCodes: string[] }[] => {
  const qrCodesByActivity: { activityId: string; qrCodes: string[] }[] = []

  for (const activity of enrollment.activities) {
    if (activity.eventType === 'SingleEvent') {
      const quantity = enrollment.activityQuantities?.[activity.id] || 1
      const qrCodes: string[] = []

      for (let ticketIndex = 1; ticketIndex <= quantity; ticketIndex++) {
        const request: QRRequestSingleEvent = {
          type: 'singleEvent',
          data: {
            enrollmentId: enrollment.id,
            ticketIndex
          }
        }
        qrCodes.push(getQRCode(request, timestamp))
      }

      qrCodesByActivity.push({
        activityId: activity.id,
        qrCodes
      })
    } else {
      // For regular activities, use the existing 'visit' type
      const request: QRRequestVisit = {
        type: 'visit',
        data: {
          enrollmentId: enrollment.id
        }
      }
      qrCodesByActivity.push({
        activityId: activity.id,
        qrCodes: [getQRCode(request, timestamp)]
      })
    }
  }

  return qrCodesByActivity
}
